/**
 * Helpers for locating which page of a document contains a cited passage.
 *
 * Citations for Office documents carry no page anchor (the indexer only emits `#page=N` for
 * PDFs), so the viewer has to find the passage itself by searching the PDF's text layer.
 */

/** Upper bound on pages inspected, so a very long document can't tie up the main thread. */
export const MAX_SCAN_PAGES = 200;

/** Page-coverage below this is treated as "not this page". */
export const COVERAGE_THRESHOLD = 0.4;

/**
 * Order in which pages should be inspected: the expected page first, then the rest in order.
 *
 * Checking the expected page first means a PDF with a good `#page=N` anchor matches on the very
 * first look and the scan stops there — the common case costs one page of work.
 */
export function pageScanOrder(
  startPage: number,
  numPages: number,
  maxPages: number = MAX_SCAN_PAGES,
): number[] {
  if (numPages <= 0) return [];
  const first = Math.min(Math.max(Math.trunc(startPage) || 1, 1), numPages);
  const order = [first];
  for (let page = 1; page <= numPages; page++) {
    if (page !== first) order.push(page);
  }
  return order.slice(0, maxPages);
}

/**
 * Fraction of the passage's distinct tokens that appear on a page.
 *
 * Deliberately a coverage measure rather than the sliding window used to highlight a span within
 * a page: a slide often holds fewer words than the citation itself, and a window wider than the
 * page would score zero on exactly the page we are looking for.
 */
export function tokenCoverage(pageTokens: string[], searchTokens: string[]): number {
  const wanted = new Set(searchTokens);
  if (wanted.size === 0) return 0;
  const available = new Set(pageTokens);
  let hits = 0;
  for (const token of wanted) {
    if (available.has(token)) hits++;
  }
  return hits / wanted.size;
}
